/**
 * Human-readable labels for the domain enums.
 *
 * These live beside the enums rather than in the web app so that a new enum
 * member cannot ship without a label — the `Record<Enum, string>` types below
 * make an omission a type error, in either app.
 *
 * Wording is carried over verbatim from the mocked FE (`catMeta`, `intentMeta`,
 * `actionMeta`, `segMeta`) so the rebuilt UI reads exactly as the approved one.
 */
import type { ActivityKind } from './dashboard.js';
import type {
  ExportFormat,
  LeadCategory,
  NextAction,
  NotificationSeverity,
  Salutation,
  CallDisposition,
  CallOutcome,
  UserIntent,
} from './enums.js';

export const salutationLabels: Record<Salutation, string> = {
  MR: 'Mr',
  MS: 'Ms',
  MRS: 'Mrs',
  MX: 'Mx',
  DR: 'Dr',
  PROF: 'Prof',
  // A word, not a dash. This appears in the contacts page's salutation
  // dropdown, where every other option is a word and a lone "—" reads as a
  // separator rather than a choice.
  NONE: 'None',
};

export const callOutcomeLabels: Record<CallOutcome, string> = {
  PENDING: 'Not called',
  DIALING: 'Calling…',
  ANSWERED: 'Answered',
  MISSED: 'Missed',
  FAILED: 'Failed',
};

/**
 * The detail under an outcome, in the operator's words. COMPLETED is the
 * platform's word for a call that was picked up and ended normally.
 */
export const callDispositionLabels: Record<CallDisposition, string> = {
  COMPLETED: 'Answered',
  NO_ANSWER: 'No answer',
  BUSY: 'Busy / declined',
  FAILED: 'Failed',
  PLATFORM_TERMINATED: 'Stopped by platform',
};

export const leadCategoryLabels: Record<LeadCategory, string> = {
  HOT_LEAD: 'Hot lead',
  WARM_LEAD: 'Warm lead',
  NOT_INTERESTED: 'Not interested',
  DO_NOT_CALL: 'Do not call',
  UNKNOWN: 'Unclassified',
};

export const userIntentLabels: Record<UserIntent, string> = {
  REQUEST_QUOTE: 'Requested quote',
  REQUEST_EMAIL_INFO: 'Requested email info',
  REQUEST_CALLBACK: 'Requested callback',
  BOOK_VISIT: 'Wants site visit',
  NOT_INTERESTED: 'Not interested',
  OPT_OUT: 'Opt-out',
  UNKNOWN: '—',
};

export const nextActionLabels: Record<NextAction, string> = {
  BOOK_APPOINTMENT: 'Book appointment',
  SEND_EMAIL: 'Send info email',
  SCHEDULE_FOLLOW_UP: 'Schedule follow-up',
  MARK_DNC: 'Do Not Call',
  NO_ACTION: 'No action',
};

export const notificationSeverityLabels: Record<NotificationSeverity, string> = {
  INFO: 'Info',
  OK: 'Good',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
};

export const exportFormatLabels: Record<ExportFormat, string> = {
  CSV: 'CSV',
  XLSX: 'Excel (XLSX)',
};

/**
 * How each activity row reads (S11).
 *
 * A verb phrase completing "<subject> …", so the feed composes as the mock does
 * — emphasised subject, plain remainder — without the server sending prose.
 */
export const activityKindLabels: Record<ActivityKind, string> = {
  CALL_ANSWERED: 'answered',
  CALL_MISSED: 'did not answer',
  CALL_FAILED: 'could not be reached',
  INTERACTION_ANALYSED: 'was analysed',
  CONTACT_SUPPRESSED: 'asked not to be called again',
  AGENT_UPDATED: 'was reconfigured',
};
